import { enumValue, convertPagination } from '~/util';

class PatientService {
    static EVENTS = {
        patient_updated: 'PatientService.patientUpdated',
        notes_updated: 'PatientService.notesUpdated',
        reminders_updated: 'PatientService.remindersUpdated',
        uploads_updated: 'PatientService.uploadsUpdated',
        risk_gap_response: 'PatientService.riskGapResponse',
        timely_action_counts_updated: 'PatientService.timelyActionCountsUpdated',
    };

    constructor(props) {
        this.props = {
            api: props.api,
            events: props.events,
        };
        this.props.events.init(PatientService.EVENTS);
        this.state = {
            patient: {},
        };
    }

    static setRiskGapAnswer(gap) {
        gap.uiAnswer = null;
        if (gap?.submission_answer?.answer) {
            if (gap.submission_answer.answer === 1) gap.uiAnswer = true;
            if (gap.submission_answer.answer === 2) gap.uiAnswer = false;
        }
        return gap;
    }

    storeSubmission(patientId, submission) {
        if (this.state.patient[patientId].submissions.some((x) => x.id === submission.id)) {
            this.state.patient[patientId].submissions = this.state.patient[
                patientId
            ].submissions.map((x) => {
                if (x.id === submission.id) {
                    return submission;
                }
                return x;
            });
            this.props.events.publish(PatientService.EVENTS.uploads_updated);
        } else {
            this.state.patient[patientId].submissions.push(submission);
            this.props.events.publish(PatientService.EVENTS.patient_updated);
        }
    }

    async addSubmission(params) {
        const result = await this.props.api.call({
            method: 'post',
            url: '/api/submissions',
            data: params,
        });
        this.storeSubmission(params.patient.id, result.data);

        return result.data;
    }

    async submitSubmission(params) {
        const result = await this.props.api.call({
            method: 'post',
            url: `/api/submissions/submit`,
            data: params,
        });

        this.state.patient[params.patient.id].submissions = this.state.patient[
            params.patient.id
        ].submissions.filter((x) => x.id !== params.id);
        this.props.events.publish(PatientService.EVENTS.uploads_updated);
        this.props.events.publish(PatientService.EVENTS.notes_updated);

        return result.data;
    }

    async updateSubmission(id, params) {
        const result = await this.props.api.call({
            method: 'put',
            url: `/api/submissions/${id}`,
            data: params,
        });
        this.storeSubmission(params.patient.id, result.data);

        return result.data;
    }

    async getGap(gapId, gapType) {
        let url = '';
        let gapProp = '';

        switch (gapType) {
            case enumValue(['gap', 'type', 'Risk Gap']):
                url = '/api/risk-gaps/';
                gapProp = 'risk_gaps';
                break;
            case enumValue(['gap', 'type', 'FOC Gap']):
                url = '/api/foc-gaps/';
                gapProp = 'foc_gaps';
                break;
            case enumValue(['gap', 'type', 'Quality Gap']):
                url = '/api/quality-gaps/';
                gapProp = 'quality_gaps';
                break;
            default:
                return;
        }

        const result = await this.props.api.call({
            method: 'get',
            url: url + gapId,
        });
        this.state.patient[result.data.patient?.split('/')[3]]?.[gapProp].forEach((x, i, a) => {
            if (x.id === gapId) {
                a[i] = PatientService.setRiskGapAnswer(result.data);
            }
        });
        this.props.events.publish(PatientService.EVENTS.patient_updated);
    }

    async loadSubmission(submissionId) {
        const result = await this.props.api.call({
            method: 'get',
            url: `/api/submissions/${submissionId}`,
        });
        return result.data;
    }

    async uploadMedia(patientId, gapType, files, isMIC) {
        const data = new FormData();
        data.set(
            'data',
            JSON.stringify({
                patient: `/api/patients/${patientId}`,
                gapType,
            })
        );
        files.forEach((file, index) => data.append(`file${index + 1}`, file));
        const headers = {};
        if (isMIC) headers['x-mic-action'] = true;
        const result = await this.props.api.call({
            method: 'post',
            url: '/api/submissions',
            data,
            headers,
        });
        this.storeSubmission(patientId, result.data);
        this.props.events.publish(PatientService.EVENTS.notes_updated);
    }

    async previewMedia(mediaId) {
        const result = await this.props.api.call({
            method: 'get',
            url: `/api/media/${mediaId}/view`,
        });
        return result.data;
    }

    async removeMedia(file) {
        await this.props.api.call({
            method: 'delete',
            url: `/api/media/${file}`,
        });
        this.props.events.publish(PatientService.EVENTS.patient_updated);
        this.props.events.publish(PatientService.EVENTS.uploads_updated);
        this.props.events.publish(PatientService.EVENTS.notes_updated);
    }

    async addReminder(entity, description, dueAt) {
        const data = {
            ...entity,
            description,
            due_at: dueAt,
        };
        await this.props.api.call({
            method: 'post',
            url: '/api/tasks',
            data,
        });
        this.props.events.publish(PatientService.EVENTS.reminders_updated);
    }

    async editReminder(reminder, data, delayPublish) {
        await this.props.api.call({
            method: 'put',
            url: `/api/tasks/${reminder.id}`,
            data,
        });
        if (!delayPublish) {
            this.props.events.publish(PatientService.EVENTS.reminders_updated);
            this.getGap(
                reminder.risk_gap?.id ?? reminder.foc_gap?.id ?? reminder.quality_gap?.id,
                reminder.type
            );
        }
    }

    async getReminders(entity, status) {
        const statusUrl = status
            ? `task_status[]=${enumValue([
                  'task',
                  'status',
                  'Completed',
              ])}&task_status[]=${enumValue([
                  'task',
                  'status',
                  'Dismissed',
              ])}&task_status[]=${enumValue(['task', 'status', 'Missed'])}`
            : `task_status[]=${enumValue([
                  'task',
                  'status',
                  'Not Started',
              ])}&task_status[]=${enumValue(['task', 'status', 'In Progress'])}`;
        const result = await this.props.api.call({
            method: 'get',
            url: `/api/tasks?${entity}&${statusUrl}`,
            data: {},
            headers: {
                'Content-Type': 'application/ld+json',
            },
        });
        return {
            results: result.data['hydra:member'],
            pagination: convertPagination(result.data),
        };
    }

    async editFilename(id, data) {
        await this.props.api.call({
            method: 'put',
            url: `/api/media/${id}`,
            data,
        });
        this.props.events.publish(PatientService.EVENTS.uploads_updated);
        this.props.events.publish(PatientService.EVENTS.notes_updated);
    }
}

export { PatientService };
